import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  draftChangedBody,
  saveSeasonRegistrationDraftOnExit,
  useDiscardSeasonRegistrationDraft,
  useSaveSeasonRegistrationDraft,
  type DraftChangedBody,
} from "../../../lib/queries/seasonRegistration";
import type { ID } from "../../../lib/id";
import { serializeDraft } from "./draftPersist";
import type { DraftState } from "./draftTypes";

/** How long the draft must sit still before it is saved. */
export const AUTOSAVE_DELAY_MS = 2000;
/** …but never longer than this after the first unsaved change: continuous typing still saves. */
export const AUTOSAVE_MAX_WAIT_MS = 15000;

export type SaveStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: number }
  | { kind: "failed"; error: unknown }
  /** Another save came first; autosave stops until the reader decides. */
  | { kind: "conflict"; body: DraftChangedBody };

/**
 * useDraftAutosave keeps the server's rozpracovaná registrace in step with
 * the page's draft (docs/rozpracovana-registrace.md §10).
 *
 * One save at a time, always of the LATEST draft: a change arriving while a
 * save is in flight is saved right after it, so revisions never race each
 * other. A draft with nothing left in it deletes the stored one instead of
 * saving an empty shell. A save refused because another one came first
 * (`draft_changed`) pauses everything until `overwrite` or `adopt`.
 *
 * `enabled` is false for a reader (only the leader writes) and while a
 * stored draft waits for „Pokračovat" / „Začít znovu", so nothing here can
 * overwrite it before the leader has answered.
 */
export function useDraftAutosave({ teamId, draft, enabled }: { teamId: ID; draft: DraftState; enabled: boolean }) {
  const save = useSaveSeasonRegistrationDraft(teamId);
  const discard = useDiscardSeasonRegistrationDraft(teamId);
  const saveAsync = save.mutateAsync;
  const discardAsync = discard.mutateAsync;

  const payload = useMemo(() => serializeDraft(draft), [draft]);
  const json = useMemo(() => JSON.stringify(payload), [payload]);
  const latest = useRef({ json, payload });
  latest.current = { json, payload };

  /** The stored revision this page builds on; 0 when nothing is stored. */
  const revision = useRef(0);
  /** The draft as last stored (or, for an empty one, as last known to need nothing). */
  const stored = useRef<string | null>(null);
  const inflight = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  /** Held while a submit is in flight: a save landing then would outlive it. */
  const held = useRef(false);
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });
  const paused = status.kind === "conflict";

  const isSynced = useCallback(
    (cur: { json: string; payload: { rows: unknown[] } }) =>
      cur.json === stored.current || (cur.payload.rows.length === 0 && revision.current === 0),
    [],
  );

  const flush = useCallback((): Promise<void> => {
    if (held.current) return Promise.resolve();
    if (inflight.current) {
      again.current = true;
      return inflight.current;
    }
    const run = async () => {
      do {
        again.current = false;
        const cur = latest.current;
        if (isSynced(cur)) continue;
        try {
          if (cur.payload.rows.length === 0) {
            // With the revision: another tab may have saved work since.
            await discardAsync({ revision: revision.current });
            revision.current = 0;
            stored.current = cur.json;
            setStatus({ kind: "idle" });
            continue;
          }
          setStatus({ kind: "saving" });
          const v = await saveAsync({ revision: revision.current, payload: cur.payload });
          revision.current = v.revision;
          stored.current = cur.json;
          setStatus({ kind: "saved", at: v.updated_ms });
        } catch (error) {
          const body = draftChangedBody(error);
          setStatus(body ? { kind: "conflict", body } : { kind: "failed", error });
          return;
        }
      } while (again.current && !held.current);
    };
    const p = run().finally(() => {
      inflight.current = null;
    });
    inflight.current = p;
    return p;
  }, [saveAsync, discardAsync, isSynced]);

  /** When the oldest unsaved change was made, for the max wait. */
  const pendingSince = useRef<number | null>(null);
  useEffect(() => {
    if (!enabled || paused || isSynced(latest.current)) {
      pendingSince.current = null;
      return;
    }
    const now = Date.now();
    pendingSince.current ??= now;
    const delay = Math.max(0, Math.min(AUTOSAVE_DELAY_MS, pendingSince.current + AUTOSAVE_MAX_WAIT_MS - now));
    const t = setTimeout(() => {
      pendingSince.current = null;
      void flush();
    }, delay);
    return () => clearTimeout(t);
  }, [json, enabled, paused, flush, isSynced]);

  // Leaving within the delay: one last save that outlives the page.
  useEffect(() => {
    if (!enabled || paused) return;
    const onHide = () => {
      const cur = latest.current;
      if (held.current || isSynced(cur) || cur.payload.rows.length === 0) return;
      saveSeasonRegistrationDraftOnExit(teamId, { revision: revision.current, payload: cur.payload });
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [enabled, paused, teamId, isSynced]);

  /** Start building on a stored revision (0: nothing stored). */
  const begin = useCallback((rev: number) => {
    revision.current = rev;
    stored.current = null;
    setStatus({ kind: "idle" });
  }, []);

  /**
   * Before a submit: let a save in flight land, then hold further saves.
   * Returns the revision the submit consumes, if any.
   */
  const holdForSubmit = useCallback(async (): Promise<number | undefined> => {
    if (inflight.current) await inflight.current;
    held.current = true;
    return revision.current > 0 ? revision.current : undefined;
  }, []);

  /** After a submit: on success the server deleted the draft with the write. */
  const releaseAfterSubmit = useCallback(
    (ok: boolean) => {
      held.current = false;
      if (ok) {
        revision.current = 0;
        stored.current = null;
        setStatus({ kind: "idle" });
        return;
      }
      void flush();
    },
    [flush],
  );

  /** „Přepsat": build on whatever is stored now and save over it. */
  const overwrite = useCallback(() => {
    if (status.kind !== "conflict") return;
    revision.current = status.body.revision;
    stored.current = null;
    setStatus({ kind: "idle" });
    void flush();
  }, [status, flush]);

  return {
    status,
    /**
     * The server holds exactly what is on screen. Informational: the page
     * warns on leaving whenever anything is unsent, saved or not.
     */
    synced: enabled && !paused && isSynced(latest.current),
    begin,
    holdForSubmit,
    releaseAfterSubmit,
    overwrite,
    /**
     * Save now, without waiting for the pause: the leader confirmed leaving
     * the page with changes the debounce had not reached yet. The request
     * outlives the component; nothing when disabled or paused.
     */
    flushNow: useCallback(() => (enabled && !paused ? flush() : Promise.resolve()), [enabled, paused, flush]),
  };
}
